const normalizeName = (value) => String(value || "").trim().toLowerCase();

export const buildWheelEntries = (inputNames, clusterNames) => {
  const safeNames = Array.isArray(inputNames) ? inputNames : [];
  if (!safeNames.length) return [];

  if (!clusterNames) {
    const sliceSpan = 360 / safeNames.length;
    return safeNames.map((name, index) => ({
      id: `slice-${index}-${normalizeName(name)}`,
      name,
      count: 1,
      sourceIndexes: [index],
      angleStart: index * sliceSpan,
      angleSpan: sliceSpan,
    }));
  }

  const grouped = new Map();
  safeNames.forEach((name, index) => {
    const normalized = normalizeName(name);
    const existing = grouped.get(normalized);

    if (!existing) {
      grouped.set(normalized, {
        id: `cluster-${index}-${normalized}`,
        name,
        count: 1,
        sourceIndexes: [index],
      });
      return;
    }

    existing.count += 1;
    existing.sourceIndexes.push(index);
  });

  let currentStart = 0;
  return Array.from(grouped.values()).map((entry) => {
    const angleSpan = (entry.count / safeNames.length) * 360;
    const withAngles = {
      ...entry,
      angleStart: currentStart,
      angleSpan,
    };
    currentStart += angleSpan;
    return withAngles;
  });
};

const mulberry32 = (seed) => {
  let value = seed >>> 0;
  return () => {
    value |= 0;
    value = (value + 0x6d2b79f5) | 0;
    let t = Math.imul(value ^ (value >>> 15), 1 | value);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const hashSeed = (seed) => {
  const input = String(seed ?? "");
  let hash = 2166136261;

  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
};

export const createSeededRandom = (seed) => {
  if (seed === undefined || seed === null || seed === "") {
    return Math.random;
  }

  return mulberry32(hashSeed(seed));
};

export const getSliceOffset = (sliceAngle, edgeBiasEnabled, random = Math.random) => {
  const edgeInset = sliceAngle * 0.075;
  const safeSpan = sliceAngle - edgeInset * 2;

  if (!edgeBiasEnabled || safeSpan <= 0) {
    return edgeInset + random() * safeSpan;
  }

  const edgeZoneWidth = safeSpan * 0.22;
  const distanceFromEdge = random() * edgeZoneWidth;
  const favorStartEdge = random() < 0.5;

  return favorStartEdge
    ? edgeInset + distanceFromEdge
    : sliceAngle - edgeInset - distanceFromEdge;
};

export const selectWheelWinnerFromEntries = ({
  names,
  wheelEntries,
  winnerIndex,
  random = Math.random,
}) => {
  const safeNames = Array.isArray(names) ? names : [];
  const safeWheelEntries = Array.isArray(wheelEntries) ? wheelEntries : [];

  if (!safeNames.length || !safeWheelEntries.length) {
    return null;
  }

  let winnerEntryIndex = 0;
  let winnerRawIndex = 0;

  if (typeof winnerIndex === "number" && winnerIndex >= 0) {
    const matchedEntryIndex = safeWheelEntries.findIndex((entry) =>
      entry.sourceIndexes.includes(winnerIndex),
    );
    winnerEntryIndex = matchedEntryIndex >= 0 ? matchedEntryIndex : 0;
    winnerRawIndex = winnerIndex;
  } else {
    const rawRandomIndex = Math.floor(random() * safeNames.length);
    const matchedEntryIndex = safeWheelEntries.findIndex((entry) =>
      entry.sourceIndexes.includes(rawRandomIndex),
    );
    winnerEntryIndex = matchedEntryIndex >= 0 ? matchedEntryIndex : 0;
    const winnerEntry = safeWheelEntries[winnerEntryIndex];
    winnerRawIndex = winnerEntry.sourceIndexes[
      Math.floor(random() * winnerEntry.sourceIndexes.length)
    ];
  }

  const winnerEntry = safeWheelEntries[winnerEntryIndex];

  return {
    winnerEntryIndex,
    rawIndex: winnerRawIndex,
    name: winnerEntry?.name ?? "",
    winnerEntry,
  };
};

export const resolveWheelWinner = ({
  names,
  settings,
  winnerIndex,
  random = Math.random,
}) => {
  const wheelEntries = buildWheelEntries(names, Boolean(settings?.clusterNames));
  const outcome = selectWheelWinnerFromEntries({
    names,
    wheelEntries,
    winnerIndex,
    random,
  });

  return outcome ? { ...outcome, wheelEntries } : null;
};